import { useMemo, useState } from 'react';

import { BsShieldX, BsTrash } from 'react-icons/bs';
import { HiPlus } from 'react-icons/hi2';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import Table from '#globalComponents/Table/Table';
import TableFilterToolbar from '#globalComponents/Table/TableFilterToolbar';
import { useError } from '#globalContexts/ErrorContext';
import { useClampPage } from '#globalHooks/useClampPage';
import { useScopedSelection } from '#globalHooks/useScopedSelection';
import { useUrlFilters } from '#globalHooks/useUrlFilters';

import { BLACKLIST_FILTER_SCHEMA, BLACKLIST_PAGE_SIZE } from '../../constants/tableFilters';
import { useBulkDeleteBlacklistEntries, useDeleteBlacklistEntry } from '../../models/mutations';
import { useContactBlacklist } from '../../models/queries';

import { buildBlacklistColumns } from './blacklistColumns';
import BlacklistEntryForm from './BlacklistEntryForm';

const EMPTY_ENTRIES = [];

const formatCount = (count) => count.toLocaleString('de-DE');

const ContactBlacklistPage = () => {
	const { showError } = useError();
	const urlFilters = useUrlFilters(BLACKLIST_FILTER_SCHEMA);
	const { filters, filtersKey, isFiltered, setFilters, page, setPage } = urlFilters;

	const { data, isLoading, isFetching } = useContactBlacklist({ page, filters });
	const entries = data?.entries ?? EMPTY_ENTRIES;
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / BLACKLIST_PAGE_SIZE));
	const deleteMutation = useDeleteBlacklistEntry();
	const bulkDeleteMutation = useBulkDeleteBlacklistEntries();

	const [showCreate, setShowCreate] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [showBulkDelete, setShowBulkDelete] = useState(false);

	useClampPage({ page, totalPages, setPage, isReady: !isFetching });

	const selection = useScopedSelection(entries, {
		totalCount: total,
		filters,
		resetKey: filtersKey,
		entityLabel: { singular: 'Eintrag', plural: 'Einträge', gender: 'm' },
	});

	const handleCreated = () => {
		// Entries are sorted newest-first, so the new one lives on page 1.
		setPage(1);
		setShowCreate(false);
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
			setDeleteTarget(null);
		} catch (error) {
			showError({ error, title: 'Eintrag konnte nicht entfernt werden' });
		}
	};

	const handleBulkDelete = async () => {
		const scope = selection.toScope();
		if (!scope) return;
		await bulkDeleteMutation.mutateAsync(scope);
		setShowBulkDelete(false);
		selection.clear();
	};

	const columns = useMemo(
		() => buildBlacklistColumns({ filters, onFilterChange: setFilters, onDelete: setDeleteTarget }),
		[filters, setFilters],
	);

	const isEmpty = !isLoading && total === 0 && !isFiltered;

	return (
		<Page
			width="full"
			scrollMode="content"
			className="overflow-x-hidden"
			header={
				<PageHeader breadcrumbs={[{ label: 'Kontaktlisten', to: '/contact-lists' }, 'Blacklist']}>
					<Button onClick={() => setShowCreate(true)} variant="solid" color="primary" size="sm">
						<HiPlus className="mr-1.5 h-3.5 w-3.5" />
						Eintrag hinzufügen
					</Button>
				</PageHeader>
			}
		>
			<div className="flex-1 min-h-0 min-w-0 flex flex-col">
				<Card className="flex items-start gap-2.5 px-3.5 py-2.5 mb-3.5">
					<BsShieldX className="w-4 h-4 text-bgBase-800 mt-0.5 flex-shrink-0" />
					<div>
						<h1 className="text-sm font-bold text-text tracking-tight">Blacklist</h1>
						<p className="text-[12px] text-bgBase-800">
							Domains und E-Mail-Adressen, die nie per E-Mail oder Brief angeschrieben werden.
						</p>
					</div>
				</Card>

				{showCreate && <BlacklistEntryForm onCreated={handleCreated} onClose={() => setShowCreate(false)} />}

				{isEmpty ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<BsShieldX className="w-8 h-8 text-bgBase-800" />
						<h3 className="text-sm font-semibold text-text">Keine Einträge auf der Blacklist</h3>
						<p className="text-xs text-bgBase-800">
							Fügen Sie Domains oder E-Mail-Adressen hinzu, die nie kontaktiert werden sollen.
						</p>
					</div>
				) : (
					<>
						<TableFilterToolbar
							urlFilters={urlFilters}
							schema={BLACKLIST_FILTER_SCHEMA}
							searchPlaceholder="E-Mail oder Domain suchen …"
							resultLabel={`${formatCount(total)} Einträge`}
						/>
						<Table
							layoutKey="contactBlacklist"
							rows={entries}
							columns={columns}
							isLoading={isLoading}
							emptyLabel="Keine Einträge entsprechen den aktiven Filtern"
							selection={selection.tableSelection}
							pagination={{
								currentPage: page,
								totalPages,
								onPageChange: setPage,
								totalItems: total,
								pageSize: BLACKLIST_PAGE_SIZE,
							}}
							bulkBar={
								<BulkActionsBar
									selectedCount={selection.count}
									entityLabel={selection.entityLabel}
									onClear={selection.clear}
									actions={[
										{
											key: 'remove',
											icon: <BsTrash />,
											label: 'Entfernen',
											onClick: () => setShowBulkDelete(true),
										},
									]}
								/>
							}
						/>
					</>
				)}
			</div>

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
				title="Eintrag entfernen"
				message={`Möchten Sie "${deleteTarget?.value}" wirklich von der Blacklist entfernen?`}
				confirmText="Entfernen"
				cancelText="Abbrechen"
				isLoading={deleteMutation.isPending}
			/>

			<ConfirmModal
				isOpen={showBulkDelete}
				onClose={() => setShowBulkDelete(false)}
				onConfirm={handleBulkDelete}
				title={
					selection.count === 1 ? 'Eintrag entfernen' : `${formatCount(selection.count)} Einträge entfernen`
				}
				message={
					selection.count === 1
						? 'Möchten Sie diesen Eintrag wirklich von der Blacklist entfernen?'
						: `Möchten Sie ${formatCount(selection.count)} Einträge wirklich von der Blacklist entfernen?`
				}
				confirmText="Entfernen"
				cancelText="Abbrechen"
				isLoading={bulkDeleteMutation.isPending}
			/>
		</Page>
	);
};

export default ContactBlacklistPage;
